import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useParams, useLocation, Navigate } from 'react-router-dom';
import MainPageHome from "./Home";
import Brokerpages from './Brokerpages';
import MainLayout from './layout/MainLayout/MainLayout';
import appversion from "../package.json";
import "./App.css";
import { CookieSetting } from "cookiesetting-component";
import Fallback from "./components/Fallback/index";

// Lazy load components
const FlyerViewer = lazy(() => import("./layout/FlyerhtmlPage/Flyerhtml"));
const ProductFlyers = lazy(() => import("./layout/ProductFlyers"));
const TexasDisclosure = lazy(() => import("./components/TexasDisclosure/index"));

// URL redirect mappings - now includes the full target path
const urlRedirects = {
  'LoanGuyBill': '/partner/williamboehm',
  // Add more mappings as needed - you can specify full paths now
  // 'oldUrl': '/partner/newUrl',
  // 'anotherOldUrl': '/some/other/path',
};

// 1. Create a helper function to find case-insensitive redirect
const findRedirectTarget = (brokerUrl) => {
  if (!brokerUrl) return null;
  
  // First try exact match (for performance)
  if (urlRedirects[brokerUrl]) {
    return urlRedirects[brokerUrl];
  }
  
  // Then try case-insensitive match
  const lowerCaseBrokerUrl = brokerUrl.toLowerCase();
  const matchingKey = Object.keys(urlRedirects).find(
    key => key.toLowerCase() === lowerCaseBrokerUrl
  );
  
  return matchingKey ? urlRedirects[matchingKey] : null;
};

// 2. Update the RedirectHandler component
const RedirectHandler = ({ brokerUrl }) => {
  const location = useLocation();
  
  useEffect(() => {
    const redirectTarget = findRedirectTarget(brokerUrl);
    
    if (redirectTarget) {
      // Check if redirectTarget is a full path or just a broker name
      let newPath;
      if (redirectTarget.startsWith('/')) {
        // Full path provided - use as is
        newPath = redirectTarget;
      } else {
        // Just broker name - replace in current path structure
        newPath = location.pathname.replace(`/${brokerUrl}/`, `/${redirectTarget}/`);
        
        // If the above didn't work (no trailing slash), try without trailing slash
        if (newPath === location.pathname) {
          newPath = location.pathname.replace(`/${brokerUrl}`, `/${redirectTarget}`);
        }
      }
      
      // Ensure trailing slash if original had one
      if (location.pathname.endsWith('/') && !newPath.endsWith('/')) {
        newPath += '/';
      }
      
      const fullNewPath = newPath + location.search + location.hash;
      
      console.log('Redirecting from:', location.pathname, 'to:', newPath);
      
      // Use window.location.replace for immediate redirect
      window.location.replace(fullNewPath);
    }
  }, [brokerUrl, location]);
  
  // Show loading while redirect is happening
  return <Fallback />;
};

// AppContent component that uses useLocation
const AppContent = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const flyerUrl = urlParams.get("flyerUrl");
  const pdfPath = urlParams.get("pdfUrl");

  // Special case for viewer mode (no header/footer)
  if (flyerUrl) {
    let loID = "";
    let categoryPath = "";
    // Extract loID and categoryPath from flyerUrl
    const flyerUrlRegex = /loan_officers\/([^/]+)\/Flyers\/(.*?\/.*?\/)/;
    const match = flyerUrl.match(flyerUrlRegex);
    if (match) {
      loID = match[1]; // Extract loID (e.g., "P508")
      categoryPath = match[2].slice(0, -1); // Extract categoryPath
    }
    // Get the broker URL from the path if present
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const brokerUrl = pathSegments.length > 0 ? pathSegments[0] : null;

    // We'll pass brokerUrl to the FlyerViewer component
    // The component will handle fetching broker data internally
    return (
      <Suspense fallback={<Fallback />}>
        <FlyerViewer
          flyerUrl={flyerUrl}
          brokerUrl={brokerUrl}
          loID={loID}
          categoryPath={categoryPath}
          pdfPath={pdfPath}
        />
      </Suspense>
    );
  }
  // If no flyerUrl, render the normal routes with MainLayout
  return (
    <Routes>
      <Route path="/" element={<MainPageWrapper />} />
      {/* Routes with partner prefix */}
      <Route path="/partner/:brokerUrl" element={<BrokerPageWrapper />} />
      <Route path="/partner/:brokerUrl/:loanProduct" element={<ProductFlyersPageWrapper />} />
      <Route path="/partner/:brokerUrl/others/:loanProduct" element={<ProductFlyersPageWrapper />} />
      <Route path="/partner/:brokerUrl/texas-disclosure/" element={<TexasDisclosurePageWrapper />} />
      {/* Routes without partner prefix (for backward compatibility) */}
      <Route path="/:brokerUrl" element={<BrokerPageWrapper />} />
      <Route path="/:brokerUrl/:loanProduct" element={<ProductFlyersPageWrapper />} />
      <Route path="/:brokerUrl/others/:loanProduct" element={<ProductFlyersPageWrapper />} />
      <Route path="/:brokerUrl/texas-disclosure/" element={<TexasDisclosurePageWrapper />} />
      {/* 404 page path */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

// Helper function to clean NMLS numbers by removing "# " pattern
const cleanNMLSNumber = (text) => {
  if (!text || typeof text !== 'string') return text;
  // Remove "# " pattern from NMLS numbers
  return text.replace(/NMLS#\s+/g, 'NMLS ');
};

// Common broker data fetching hook
const useBrokerData = (brokerUrl) => {
  const [brokerData, setBrokerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const fetchBrokerData = async () => {
      if (!brokerUrl) {
        setLoading(false);
        return;
      }

      // Check if this is a URL that should be redirected (case-insensitive)
      const redirectTarget = findRedirectTarget(brokerUrl);
      if (redirectTarget) {
        setLoading(false);
        return;
      }

      try {
        const BrokerJSON = `${process.env.REACT_APP_CDN}brokers-landingpages-resources/includes/${brokerUrl}.json`;
        const response = await fetch(BrokerJSON);
        if (!response.ok) {
          console.error(`Failed to fetch broker data: ${response.status}`);
          setShouldRedirect(true);
          return;
        }
        const data = await response.json();
        const rawBrokerData = data[0];

        // Clean up NMLS numbers in the specified fields
        const cleanedBrokerData = {
          ...rawBrokerData,
          companyName: cleanNMLSNumber(rawBrokerData?.companyName),
          branchNmls: cleanNMLSNumber(rawBrokerData?.branchNmls),
          nmls: cleanNMLSNumber(rawBrokerData?.nmls)
        };

        setBrokerData(cleanedBrokerData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching broker data:", err);
        setError(err.message);
        setShouldRedirect(true);
        setLoading(false);
      }
    };
    fetchBrokerData();
  }, [brokerUrl]);

  // Redirect to angelai.com if there's an error (but not if it's a URL redirect)
  useEffect(() => {
    if (shouldRedirect && !findRedirectTarget(brokerUrl)) {
      window.location.href = 'https://www.angelai.com/';
    }
  }, [shouldRedirect, brokerUrl]);

  return { brokerData, loading, error, shouldRedirect };
};

// Main page wrapper
const MainPageWrapper = () => {
  // Main page doesn't require broker data, so we render it directly
  return <MainPageHome />;
};

// Broker page wrapper with MainLayout
const BrokerPageWrapper = () => {
  const { brokerUrl } = useParams();
  const { brokerData, loading, error } = useBrokerData(brokerUrl);
  
  // Check for redirect after hooks are called (case-insensitive)
  if (findRedirectTarget(brokerUrl)) {
    return <RedirectHandler brokerUrl={brokerUrl} />;
  }

  return (
    <MainLayout brokerData={brokerData} loading={loading} error={error} brokerUrl={brokerUrl}>
      <Brokerpages brokerData={brokerData} loading={loading} error={error} brokerUrl={brokerUrl} />
    </MainLayout>
  );
};

// Product flyers page wrapper with MainLayout
const ProductFlyersPageWrapper = () => {
  const { brokerUrl, loanProduct } = useParams();
  const { brokerData, loading, error } = useBrokerData(brokerUrl);
  
  // Check for redirect after hooks are called (case-insensitive)
  if (findRedirectTarget(brokerUrl)) {
    return <RedirectHandler brokerUrl={brokerUrl} />;
  }

  return (
    <MainLayout brokerData={brokerData} loading={loading} error={error} brokerUrl={brokerUrl} >
      <Suspense fallback={<Fallback />}>
        {!loading && !error && brokerData && (
          <ProductFlyers brokerData={brokerData} brokerUrl={brokerUrl} />
        )}
      </Suspense>
    </MainLayout>
  );
};

// Texas Disclosure page wrapper with MainLayout
const TexasDisclosurePageWrapper = () => {
  const { brokerUrl } = useParams();
  const { brokerData, loading, error } = useBrokerData(brokerUrl);
  
  // Check for redirect after hooks are called (case-insensitive)
  if (findRedirectTarget(brokerUrl)) {
    return <RedirectHandler brokerUrl={brokerUrl} />;
  }

  return (
    <MainLayout brokerData={brokerData} loading={loading} error={error} brokerUrl={brokerUrl} >
      <Suspense fallback={<Fallback />}>
        {!loading && !error && brokerData && (
          <TexasDisclosure brokerData={brokerData} brokerUrl={brokerUrl} />
        )}
      </Suspense>
    </MainLayout>
  );
};

// Define a separate route handler component for flyer URL query parameter
// This component will be used by the Router to handle all routes when flyerUrl is present
const FlyerUrlRouteHandler = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const flyerUrl = urlParams.get("flyerUrl");
  const pdfPath = urlParams.get("pdfUrl");

  // Extract brokerUrl from the current path if available
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const brokerUrl = pathSegments.length > 0 ? pathSegments[0] : null;

  // Custom hook to fetch broker data
  const { brokerData } = useBrokerData(brokerUrl);

  // Extract loID and categoryPath from flyerUrl
  let loID = "";
  let categoryPath = "";

  if (flyerUrl) {
    const flyerUrlRegex = /loan_officers\/([^/]+)\/Flyers\/(.*?\/.*?\/)/;
    const match = flyerUrl.match(flyerUrlRegex);

    if (match) {
      loID = match[1];
      categoryPath = match[2].slice(0, -1);
    }
  }

  return (
    <Suspense fallback={<Fallback />}>
      <FlyerViewer
        flyerUrl={flyerUrl}
        brokerData={brokerData}
        loID={loID}
        categoryPath={categoryPath}
        pdfPath={pdfPath}
      />
    </Suspense>
  );
};

const App = () => {
  const baseName = process.env.REACT_APP_BASENAME;
  return (
    <>
      <Router basename={baseName} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
      </Router>
      <CookieSetting
        privacyPolicyLink={process.env.REACT_APP_PRIVACY_POLICY_LINK}
        configUrl={process.env.REACT_APP_COOKIE_SETTING_JSON}
        analytics={process.env.REACT_APP_ANALYTICS_PATH}
      />
    </>
  );
};

export default App;